/**
 * The workshop's screen in the cockpit (PLAN-ATELIER-OTTO § 16.6, after Muse): the forms Chrome of
 * the VM, shown live in the mission card. The database signs a one-minute ticket
 * (`issue_workshop_screen_ticket`: an admin to look, a named decider to act); the VM's gate checks it
 * and hands back a one-time WebSocket path and the VNC password that matches the ticket. Both answers
 * are boundaries: parsed here, never trusted as they come.
 */
import { z } from 'zod';

export const screenTicketSchema = z.object({
  ticket: z.string().regex(/^v1\.[\w-]+\.[\w-]+$/),
  screen_url: z.string().regex(/^https:\/\/[a-z0-9.-]+\/?$/),
  controls: z.boolean(),
  expires_at: z.string(),
});
export type ScreenTicket = z.infer<typeof screenTicketSchema>;

/** What the forms Chrome may reach. `open` is a fact since 08/10/2026; a gate from before says words only. */
const formsStateSchema = z.object({ mode: z.string(), open: z.boolean().optional(), last_reopen: z.string() });
type FormsState = z.infer<typeof formsStateSchema>;

export const screenSessionSchema = z.object({
  websocket: z.string().regex(/^\/websockify\?s=[\w-]+$/),
  password: z.string().min(1).max(64),
  view_only: z.boolean(),
  forms: formsStateSchema,
});
export type ScreenSession = z.infer<typeof screenSessionSchema>;

/** What the viewer needs to connect, and what the card says around it. */
export interface OpenScreen {
  websocketUrl: string;
  password: string;
  viewOnly: boolean;
  /** The gate's own words on what the forms Chrome may reach, and root's last answer. */
  formsMode: string;
  formsLastReopen: string;
  /** Whether it may reach every site; null when the gate does not say (before 08/10/2026). */
  formsOpen: boolean | null;
}

/**
 * The gate's answer to « Rouvrir à tout site » (since 08/10/2026 it waits for root): reopened, or
 * refused (a workshop holds the browser), with the browser's state after it. A 202 means root has
 * not answered in time; a gate from before answers `{requested: true}` only.
 */
export const reopenAnswerSchema = z.object({
  requested: z.literal(true),
  ok: z.boolean().optional(),
  reason: z.string().nullable().optional(),
  forms: formsStateSchema.optional(),
});
export type ReopenAnswer = z.infer<typeof reopenAnswerSchema>;

/** The screen as the gate now describes the browser. Pure. */
export function withForms(screen: OpenScreen, forms: FormsState): OpenScreen {
  return { ...screen, formsMode: forms.mode, formsLastReopen: forms.last_reopen, formsOpen: forms.open ?? null };
}

/** What the card says after a reopening, from the gate's answer. Pure. */
export function reopenNotice(answer: ReopenAnswer): { notice: string | null; error: string | null } {
  if (answer.ok === true) return { notice: 'Navigateur rouvert à tout site : tu peux te connecter.', error: null };
  if (answer.ok === false) {
    return answer.reason === 'workshop_active'
      ? { notice: null, error: "Le navigateur n'a pas été rouvert : un atelier formulaire le tient. Prends la main, ou attends la fin de l'atelier." }
      : { notice: null, error: `Le navigateur n'a pas été rouvert (${answer.reason ?? 'échec'}). Réessaie.` };
  }
  return {
    notice: 'Demande envoyée : la machine rouvre le navigateur à tout site (quelques secondes, la page en cours se ferme).',
    error: null,
  };
}

/** An address on the gate. Pure. */
export function gateUrl(screenUrl: string, path: string): string {
  return `${screenUrl.replace(/\/$/, '')}${path}`;
}

/** The viewer's connection, from a ticket and the gate's answer. Pure. */
export function openScreen(ticket: ScreenTicket, session: ScreenSession): OpenScreen {
  return {
    websocketUrl: gateUrl(ticket.screen_url, session.websocket).replace(/^https:/, 'wss:'),
    password: session.password,
    // The gate's answer follows the ticket; both must agree before anything is clickable.
    viewOnly: session.view_only || !ticket.controls,
    formsMode: session.forms.mode,
    formsLastReopen: session.forms.last_reopen,
    formsOpen: session.forms.open ?? null,
  };
}

/** A refusal of the database (RPC message) or of the gate (HTTP status), in words. Pure. */
export function screenErrorMessage(source: { message?: string; status?: number }): string {
  const message = source.message ?? '';
  if (message.includes('screen_not_configured')) return "L'écran n'est pas encore en service (clé ou adresse manquante).";
  if (message.includes('screen_controls_not_allowed')) {
    return "Pour agir dans le navigateur, il faut être décideur nommé, et avoir la main si un atelier le tient.";
  }
  if (message.includes('reserved to admins')) return "L'écran de l'atelier est réservé aux admins.";
  if (message.includes('screen_held_by_another')) {
    return "Le navigateur de l'atelier travaille pour la mission d'une autre personne : son écran ne s'ouvre qu'à elle.";
  }
  if (source.status === 401) return 'La machine a refusé le laissez-passer. Réessaie dans un instant.';
  if (source.status === 403) return "La machine refuse cette origine : l'écran ne s'ouvre que depuis le cockpit.";
  if (source.status === 503) return "Le serveur d'écran de la machine n'est pas configuré.";
  if (source.status !== undefined) return `La machine a répondu ${source.status}. Réessaie.`;
  return "La machine ne répond pas : VM éteinte, ou écran pas publié.";
}

/** The viewer's end, in words: a clean close is the user's, anything else is a cut. Pure. */
export function disconnectMessage(clean: boolean): string | null {
  return clean ? null : "Connexion à l'écran coupée. Rouvre-le pour reprendre.";
}
